import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PwaCaptureEntityPicker } from 'components/PwaCaptureEntityPicker';
import { useDispatch, useSelector } from 'react-redux';
import routes from 'routes';
import { turnDrawerOff } from 'store/app/actions';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { getReceiptStatisticsAll } from 'store/organisation/selectors';
import {
  leaveEntityOrganisationPage,
  unlockLeaveEntityNavigation,
} from 'utils/leave-entity-navigation-lock';
import {
  getPwaLastEntity,
  markSkipPwaHomeRestore,
} from 'utils/pwa-last-entity';
import {
  buildPwaEntityCaptureNavigateUrl,
  buildPwaEntityTaxReturnsNavigateUrl,
  buildPwaEntityTransactionsNavigateUrl,
  getPwaBottomNavActiveId,
} from 'utils/pwa-nav';
import {
  getPwaEntityPendingReviewCount,
  getPwaPendingReviewTotal,
} from 'utils/pwa-entity-pending-review';
import { usePwaCaptureReadyEntities } from 'utils/use-pwa-capture-entities';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { PwaIcon, type PwaIconName } from 'pwa/components/Icon';

/** Location state set when the user explicitly taps Home in the PWA bottom nav. */
export const PWA_EXPLICIT_HOME_NAV_STATE = { pwaExplicitHome: true as const };

type NavDest = 'home' | 'review' | 'capture' | 'returns' | 'more';

type NavItem = {
  id: NavDest;
  labelKey: string;
  icon: PwaIconName;
  primary?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { id: 'home', labelKey: 'navigation.home', icon: 'home' },
  { id: 'review', labelKey: 'common.review', icon: 'review' },
  {
    id: 'capture',
    labelKey: 'organisation.receipts-capture-shot',
    icon: 'camera',
    primary: true,
  },
  { id: 'returns', labelKey: 'navigation.returns', icon: 'returns' },
  { id: 'more', labelKey: 'navigation.more', icon: 'more' },
];

function organisationIdFromPath(pathname: string): string | null {
  const match = pathname.match(/\/entities\/organisation\/([^/?#]+)/);
  return match?.[1] ?? null;
}

export const PwaBottomNav: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const organisations = useSelector(getOrgOrganisations);
  const receiptStatistics = useSelector(getReceiptStatisticsAll);
  const { eligibleEntities: captureEntities, pinnedEntityIds } =
    usePwaCaptureReadyEntities(organisations);

  const [entityPickerOpen, setEntityPickerOpen] = React.useState(false);
  const [entityPickerDest, setEntityPickerDest] = React.useState<
    'review' | 'capture' | 'returns'
  >('capture');

  const reviewCount = React.useMemo(() => {
    const fromPath = organisationIdFromPath(location.pathname);
    if (fromPath) {
      const entity =
        organisations?.find((item) => item.uuid === fromPath) ??
        captureEntities.find((item) => item.uuid === fromPath);
      if (!entity) return 0;
      return getPwaEntityPendingReviewCount(entity, receiptStatistics);
    }
    return getPwaPendingReviewTotal(captureEntities, receiptStatistics);
  }, [location.pathname, organisations, captureEntities, receiptStatistics]);

  const activeId: NavDest = getPwaBottomNavActiveId(
    location.pathname,
    location.search
  ) as NavDest;

  useOverlayBackHandler(
    entityPickerOpen,
    () => setEntityPickerOpen(false),
    'pwa-bottom-nav-entity-picker',
    true
  );

  const leaveEntityTo = React.useCallback(
    (to: string, options?: { explicitHome?: boolean }) => {
      dispatch(turnDrawerOff());
      if (options?.explicitHome) {
        markSkipPwaHomeRestore();
      }
      // Lock tab-sync when leaving an entity; always soft-navigate (no full reload).
      leaveEntityOrganisationPage(location.pathname, to);
      if (options?.explicitHome) {
        navigate(to, { state: PWA_EXPLICIT_HOME_NAV_STATE });
        return;
      }
      navigate(to);
    },
    [dispatch, navigate, location.pathname]
  );

  const resolveEntityId = React.useCallback((): string | null => {
    const fromPath = organisationIdFromPath(location.pathname);
    if (fromPath) return fromPath;
    const last = getPwaLastEntity()?.organisationId;
    // Prefer most recently viewed entity (even when currently on Home).
    if (last && organisations?.some((e) => e.uuid === last)) {
      return last;
    }
    if (last && captureEntities.some((e) => e.uuid === last)) {
      return last;
    }
    return captureEntities[0]?.uuid ?? organisations?.[0]?.uuid ?? null;
  }, [location.pathname, organisations, captureEntities]);

  const navigateToEntityTab = React.useCallback(
    (dest: 'review' | 'capture' | 'returns', organisationId: string) => {
      unlockLeaveEntityNavigation();
      dispatch(turnDrawerOff());
      let url: string;
      if (dest === 'review') {
        url = buildPwaEntityTransactionsNavigateUrl(organisationId);
      } else if (dest === 'returns') {
        url = buildPwaEntityTaxReturnsNavigateUrl(organisationId);
      } else {
        url = buildPwaEntityCaptureNavigateUrl(organisationId);
      }
      navigate(url);
    },
    [dispatch, navigate]
  );

  const openEntityDest = React.useCallback(
    (dest: 'review' | 'capture' | 'returns') => {
      const id = resolveEntityId();
      if (id) {
        navigateToEntityTab(dest, id);
        return;
      }
      if (captureEntities.length === 1) {
        navigateToEntityTab(dest, captureEntities[0].uuid);
        return;
      }
      if (captureEntities.length > 1) {
        setEntityPickerDest(dest);
        setEntityPickerOpen(true);
        return;
      }
      leaveEntityTo(routes.main, { explicitHome: true });
    },
    [resolveEntityId, navigateToEntityTab, captureEntities, leaveEntityTo]
  );

  const handleSelect = (item: NavItem) => {
    if (item.id === 'more') {
      leaveEntityTo(routes.account.root);
      return;
    }
    if (item.id === 'home') {
      leaveEntityTo(routes.main, { explicitHome: true });
      return;
    }
    if (item.id === 'review' || item.id === 'capture' || item.id === 'returns') {
      openEntityDest(item.id);
    }
  };

  return (
    <>
      <nav
        className="scope-pwa-bottom-nav pwa-nav"
        data-shell="pwa"
        aria-label={t('common.pwa-bottom-nav-label')}
      >
        {NAV_ITEMS.map((item) => {
          const isActive = activeId === item.id;
          if (item.primary) {
            return (
              <button
                key={item.id}
                type="button"
                className="pwa-nav-capture"
                aria-current={isActive ? 'page' : undefined}
                aria-label={t(item.labelKey, { defaultValue: 'Capture' })}
                onClick={() => handleSelect(item)}
              >
                <span className="pwa-fab" aria-hidden>
                  <PwaIcon name="camera" size={24} stroke={2} />
                </span>
                <span>
                  {t(item.labelKey, { defaultValue: 'Capture' })}
                </span>
              </button>
            );
          }
          return (
            <button
              key={item.id}
              type="button"
              aria-current={isActive ? 'page' : undefined}
              onClick={() => handleSelect(item)}
            >
              <span className="pwa-nav-ico" aria-hidden>
                <PwaIcon
                  name={item.icon}
                  size={22}
                  stroke={isActive ? 2.1 : 1.75}
                />
              </span>
              {item.id === 'review' && reviewCount > 0 ? (
                <span
                  className="pwa-nav-badge"
                  aria-label={t('navigation.review-pending-count', {
                    count: reviewCount,
                    defaultValue: '{{count}} to review',
                  })}
                >
                  {reviewCount > 99 ? '99+' : reviewCount}
                </span>
              ) : null}
              <span>
                {t(item.labelKey, {
                  defaultValue:
                    item.id === 'review'
                      ? 'Review'
                      : item.id === 'returns'
                        ? 'Returns'
                        : item.id === 'more'
                          ? 'More'
                          : 'Home',
                })}
              </span>
            </button>
          );
        })}
      </nav>
      <PwaCaptureEntityPicker
        open={entityPickerOpen}
        entities={captureEntities}
        pinnedEntityIds={pinnedEntityIds}
        onClose={() => setEntityPickerOpen(false)}
        onSelect={(organisationId) => {
          setEntityPickerOpen(false);
          navigateToEntityTab(entityPickerDest, organisationId);
        }}
      />
    </>
  );
};

export default PwaBottomNav;
